import React from 'react';
import { useTranslation } from 'react-i18next';
import { Card, RingChart } from '../../tcn/ui-kit';
import { TCN } from '../../tcn/ui-kit/tokens';
import { formatUSD } from '../shared/format';

/**
 * Module 5 — Headline pay-out card.
 *
 * Aligned on mockup module5-payout.jsx:5-39 :
 *   - Hero card padding 20, borderRadius 18, gradient earned + green border
 *   - flex column center, gap 8
 *   - Eyebrow "Earned this month" 11/700/textMute/uppercase/0.6 letter-spacing
 *   - RingChart 160 / stroke 14 / green / sub "of potential"
 *   - Triplet vertical Earned/Potential/Gap (3 cells separated by 1px dividers)
 *
 * Inputs (from manager_current_month):
 *   - total_earned_payout_usd
 *   - total_potential_payout_usd
 *   - total_gap_payout_clear
 *   - total_earned_pct_of_potential
 *   - usdRate fallback
 */
const PayoutHeadline = ({ data, usdRate = 0.01 }) => {
  const { t } = useTranslation();
  if (!data) return null;

  const earned = Number(
    data.total_earned_payout_usd ?? (data.total_earned_payout_clear ?? 0) * usdRate
  );
  const potential = Number(
    data.total_potential_payout_usd ?? (data.total_potential_payout_clear ?? 0) * usdRate
  );
  const gap = Math.max(0, potential - earned);
  const completionPct =
    data.total_earned_pct_of_potential != null
      ? Number(data.total_earned_pct_of_potential)
      : potential > 0
      ? (earned / potential) * 100
      : 0;

  const fmt = (v) => formatUSD(v, { fractionDigits: v < 100 ? 2 : 0 });

  return (
    <Card
      padded={false}
      style={{
        padding: 20,
        borderRadius: 18,
        background: TCN.gradients.earned,
        border: `1px solid ${TCN.green}44`,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 8,
      }}
    >
      {/* Eyebrow — mockup line 13-14 */}
      <div
        style={{
          fontSize: 11,
          fontWeight: 700,
          color: TCN.textMute,
          textTransform: 'uppercase',
          letterSpacing: '0.6px',
        }}
      >
        {t('tcn.m5.headline.label', 'Earned this month')}
      </div>

      {/* RingChart 160/14 green — mockup line 15 */}
      <RingChart
        value={completionPct}
        target={100}
        size={160}
        stroke={14}
        color="green"
        sub={t('tcn.m5.headline.ringSub', 'of potential')}
      />

      {/* Triplet vertical — mockup lines 16-39 */}
      <div style={{ marginTop: 4, display: 'flex', gap: 16 }}>
        <Cell
          label={t('tcn.m5.headline.earned', 'Earned')}
          value={fmt(earned)}
          color={TCN.green}
        />
        <Divider />
        <Cell
          label={t('tcn.m5.headline.potential', 'Potential')}
          value={fmt(potential)}
          color={TCN.text}
        />
        <Divider />
        <Cell
          label={t('tcn.m5.headline.gap', 'Gap')}
          value={fmt(gap)}
          color={TCN.amber}
        />
      </div>
    </Card>
  );
};

const Cell = ({ label, value, color }) => (
  <div style={{ textAlign: 'center' }}>
    <div
      style={{
        fontSize: 10,
        color: TCN.textMute,
        fontWeight: 700,
        textTransform: 'uppercase',
        letterSpacing: '0.4px',
      }}
    >
      {label}
    </div>
    <div
      style={{
        fontSize: 18,
        fontWeight: 700,
        color,
        fontVariantNumeric: 'tabular-nums',
        marginTop: 2,
      }}
    >
      {value}
    </div>
  </div>
);

const Divider = () => <div style={{ width: 1, background: TCN.border }} />;

export default PayoutHeadline;
